import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, renderSlot as _renderSlot, toHandlers as _toHandlers, mergeProps as _mergeProps, createVNode as _createVNode, createSlots as _createSlots, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "message-date"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "message-content-wrapper" }
const _hoisted_4 = {
  key: 0,
  class: "message-reply-bottom"
}

import ChatMessageReactions from "../ChatMessageReactions/ChatMessageReactions.vue";
import { IconType } from "@/types/icons";
import {
  showMessageDate,
  getMessageClass,
  getMessageDate,
  getIsMessageTextType,
  getIsAutomaticMessageType,
  getMessageReplyTitle,
  getMessageContentElementId,
  skipToReplyMessage,
  getIsMessageAudioType,
} from "@/utils/message";
import { getIsMyMessageClass } from "@/utils/chat";
import {
  ChatState,
  Message,
  NonAutomaticMessage,
  Positions,
  TextMessage,
} from "@/store/chats/types";
import { useComputedValue, useLongPress } from "@/composables";
import { GetterTypes, MutationTypes } from "@/store";
import { computed, nextTick, onUnmounted, reactive, ref } from "vue";
import { getIsBlockingTextShown } from "@/utils/chat";
import { useStore } from "vuex";
import { ChatsMutationTypes } from "@/store/chats";
import { CustomClickEvent } from "@/types/app";
import { findParentNodeById, getSafeElementPosition } from "@/utils/app";
import ChatMessageReactionsModal from "../ChatMessageReactionsModal/ChatMessageReactionsModal.vue";
import BackdropModal from "@/components/modals/BackdropModal/BackdropModal.vue";
import { LayoutTypes } from "@/store/app/state";
import { useI18n } from "vue-i18n";
import { getRestrictedMessagesRef } from "@/firebase/ref";
import firebase from "@/firebase/firebase";
import ImageAttachment from "../ImageAttachment/ImageAttachment.vue";
import CheckboxSelectMessage from "@/components/checkbox/CheckboxSelectMessage.vue";
import MessageExtrasWrapper from "../MessageExtrasWrapper/MessageExtrasWrapper.vue";

interface ChatMessageContentProps {
  message: Message;
  index: number;
  visibleMessages: Message[];
}

interface Emits {
  (e: "replyToMessage", message: NonAutomaticMessage): void;
  (e: "modifyMessage", message: TextMessage): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ChatMessageContent',
  props: {
    message: {},
    index: {},
    visibleMessages: {}
  },
  emits: ["replyToMessage", "modifyMessage"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const { commit } = useStore();
const props = __props;
const isBlockingTextShown = getIsBlockingTextShown();
const authId = useComputedValue<number>(GetterTypes.GET_AUTH_ID);
const messageReactionId = useComputedValue<string | null>(
  GetterTypes.GET_MESSAGE_REACTION_ID,
);
const { t } = useI18n();
const positionObject = reactive<Positions>({
  top: 0,
  left: 0,
  width: 0,
  height: undefined,
});
const selectedMessageIds = useComputedValue<string[] | null>(
  GetterTypes.GET_SELECTED_MESSAGE_IDS,
);
const messageElementId = computed(() => `message_${props.message.id}`);
const messageContentElementId = computed(() =>
  getMessageContentElementId(props.message.id),
);
const hasShadow = ref(false);
const isReactionsModalOpen = ref(false);
const isMessageAutomatic = computed(() =>
  getIsAutomaticMessageType(props.message),
);

const textMessagePreviewText = computed(() => {
  if (getIsAutomaticMessageType(props.message)) {
    return;
  }
  const { replyToMessage } = props.message;
  if (!replyToMessage) {
    return;
  }

  if (
    getIsMessageTextType(replyToMessage) &&
    replyToMessage?.imageAttachments?.length &&
    !replyToMessage.messageText
  ) {
    return;
  }

  if (getIsMessageTextType(replyToMessage) && replyToMessage.isDeleted) {
    return t("chat.chatContainer.messages.deleting.deletedMessage");
  }
  if (getIsMessageTextType(replyToMessage)) {
    return replyToMessage.displayMessageText;
  }

  if (getIsMessageAudioType(replyToMessage) && replyToMessage.isDeleted) {
    return t("chat.chatContainer.messages.deleting.deletedAudioMessage");
  }

  return t("chat.chatContainer.messages.audioMessage");
});

const isActive = computed(
  () =>
    !isMessageAutomatic.value && messageReactionId.value === props.message.id,
);
const isControlsActive = computed(
  () => isActive.value && !isReactionsModalOpen.value,
);
const reactionsModalId = computed(() => `reactions-modal-${props.message.id}`);
const layoutType = useComputedValue<LayoutTypes>(GetterTypes.GET_LAYOUT_TYPE);
const isMobile = computed(() => layoutType.value === LayoutTypes.MOBILE);
const isTouchEndEnabled = ref(false);
const isMine = computed(() => props.message.senderId === authId.value);
const chat = useComputedValue<ChatState | undefined>(
  GetterTypes.GET_SELECTED_CHAT_STATE,
);
const firstMessage = computed(() => {
  const firstMessageId = chat.value?.messageIds[0];
  if (!firstMessageId) {
    return null;
  }
  return chat.value.messages[firstMessageId];
});
const isReactingEnabled = computed(
  () => !getIsAutomaticMessageType(props.message) && !props.message.isDeleted,
);

const isMessageContentShown = computed<boolean>(() => {
  const { message } = props;
  if (!getIsMessageTextType(message)) {
    return true;
  }

  const { messageText, othersAttachments, imageAttachments, isDeleted } =
    message;

  return Boolean(
    messageText ||
      othersAttachments ||
      imageAttachments?.length === 1 ||
      isDeleted,
  );
});

const handlePosition = () => {
  const messageContentEl = document.getElementById(
    messageContentElementId.value,
  );
  if (!messageContentEl) {
    return;
  }

  const { top, left, width, height } = getSafeElementPosition(messageContentEl);

  positionObject.left = left;
  positionObject.top = top;
  positionObject.width = width;
  positionObject.height = height;
  hasShadow.value = true;
};

const onLongPress = (event: CustomClickEvent) => {
  // if the user is in deleting messages mode, or if the message is automatic, or the message is deleted, reactions are not allowed
  if (
    selectedMessageIds.value ||
    isMessageAutomatic.value ||
    !isMobile.value ||
    !isReactingEnabled.value
  ) {
    return;
  }
  const target = event.target;
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: props.message.id,
  });

  const wrapper = findParentNodeById(target, messageElementId.value);
  if (!wrapper) {
    return;
  }

  disableScroll();
  handlePosition();
  disableTouchEnd();
};

const handleEnableTouchEnd = () => {
  isTouchEndEnabled.value = true;
};

const disableTouchEnd = () => {
  isTouchEndEnabled.value = false;
  window.addEventListener("touchend", handleEnableTouchEnd);
  window.addEventListener("mouseup", handleEnableTouchEnd);
};

const clearDisableTouchEnd = () => {
  isTouchEndEnabled.value = true;
  window.removeEventListener("touchend", handleEnableTouchEnd);
  window.removeEventListener("mouseup", handleEnableTouchEnd);
};

onUnmounted(() => {
  clearDisableTouchEnd();
  closeReactionsModal();
});

const disableScroll = () => {
  document.body.style.overflow = "hidden";
};

const hideShadow = () => {
  positionObject.left = 0;
  positionObject.top = 0;
  hasShadow.value = false;
  clearDisableTouchEnd();
};

const handlers = useLongPress({ onLongPress });

const openReactionsModal = () => {
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: props.message.id,
  });
  isReactionsModalOpen.value = true;
};

const closeReactionsModal = () => {
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: null,
  });
  isReactionsModalOpen.value = false;
};

const forwardedMessageTitle = computed(() => {
  if (isMine.value) {
    return t("chat.chatContainer.messages.forwarded.ownMessage");
  }
  return t("chat.chatContainer.messages.forwarded.message");
});

const fetchPreviousMessages = async () => {
  const message = props.message;
  if (getIsAutomaticMessageType(message) || !message.replyToMessage) {
    return;
  }

  if (!chat.value) {
    return;
  }

  const { chatId, messageIds } = chat.value;

  if (messageIds.includes(message.replyToMessage.id)) {
    skipToReplyMessage(message.replyToMessage);
    return;
  }

  if (!firstMessage.value) {
    return;
  }

  const messageSentAt = firstMessage.value.sentAt;
  const { sentAt } = message.replyToMessage;
  const replyToMessageSentAt = firebase.firestore.Timestamp.fromMillis(sentAt);
  const restrictedMessageRef = getRestrictedMessagesRef(
    chatId,
    messageSentAt,
  ).where("sentAt", ">=", replyToMessageSentAt);
  const messagesRef = await restrictedMessageRef.get();
  const messages = messagesRef.docs.map((mess) => mess.data());

  if (messages.length === 0) {
    return;
  }
  commit(MutationTypes.PREPEND_MESSAGES, { messages, chatId });
  await nextTick();
  skipToReplyMessage(message.replyToMessage);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(showMessageDate)(_ctx.visibleMessages, _ctx.index))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_unref(getMessageDate)(_ctx.message)), 1))
      : _createCommentVNode("", true),
    (!_ctx.message.hidden)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          id: messageElementId.value,
          class: _normalizeClass(["message-wrapper", {
      isBlocking: _unref(isBlockingTextShown) && _ctx.index === _ctx.visibleMessages.length - 1,
      isActive: isControlsActive.value,
    }])
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["message", _unref(getIsMyMessageClass)(_ctx.message, _unref(authId))])
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", {
                class: _normalizeClass(["message-content-wrapper-inner", { isMine: isMine.value }])
              }, [
                (!_unref(getIsAutomaticMessageType)(_ctx.message) && _ctx.message.replyToMessage)
                  ? (_openBlock(), _createBlock(MessageExtrasWrapper, {
                      key: 0,
                      title: _unref(getMessageReplyTitle)({ message: _ctx.message, authId: _unref(authId), isMine: isMine.value, t: _unref(t) }),
                      "is-mine": isMine.value,
                      icon: _unref(IconType).REPLY
                    }, {
                      default: _withCtx(() => [
                        (!_ctx.message.isForwarded)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                              _createElementVNode("div", {
                                class: "reply-text-wrapper",
                                onClick: fetchPreviousMessages
                              }, [
                                (textMessagePreviewText.value)
                                  ? (_openBlock(), _createElementBlock("span", {
                                      key: 0,
                                      class: _normalizeClass(["reply-text", { isDeleted: _ctx.message.replyToMessage.isDeleted }])
                                    }, _toDisplayString(textMessagePreviewText.value), 3))
                                  : _createCommentVNode("", true),
                                (
                    _unref(getIsMessageTextType)(_ctx.message.replyToMessage) &&
                    _ctx.message.replyToMessage?.imageAttachments?.length
                  )
                                  ? (_openBlock(), _createBlock(ImageAttachment, {
                                      key: 1,
                                      class: _normalizeClass(["replyImageAttachment", { hasNoText: !textMessagePreviewText.value }]),
                                      attachment: _ctx.message.replyToMessage.imageAttachments[0],
                                      message: _ctx.message,
                                      "is-shrinked": true
                                    }, null, 8, ["class", "attachment", "message"]))
                                  : _createCommentVNode("", true)
                              ])
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 8, ["title", "is-mine", "icon"]))
                  : _createCommentVNode("", true),
                (
              _unref(getIsMessageTextType)(_ctx.message) &&
              _ctx.message.isModified &&
              !_ctx.message.isForwarded
            )
                  ? (_openBlock(), _createBlock(MessageExtrasWrapper, {
                      key: 1,
                      title: _unref(t)('chat.chatContainer.messages.editing.modified')
                    }, null, 8, ["title"]))
                  : _createCommentVNode("", true),
                (!_unref(getIsAutomaticMessageType)(_ctx.message) && _ctx.message.isForwarded)
                  ? (_openBlock(), _createBlock(MessageExtrasWrapper, {
                      key: 2,
                      title: forwardedMessageTitle.value,
                      icon: _unref(IconType).FORWARD_MESSAGE
                    }, null, 8, ["title", "icon"]))
                  : _createCommentVNode("", true),
                _createVNode(ChatMessageReactions, {
                  message: _ctx.message,
                  "is-active": isActive.value,
                  "has-shadow": hasShadow.value,
                  "is-modal-open": isReactionsModalOpen.value,
                  "modal-id": reactionsModalId.value,
                  "element-id": messageContentElementId.value,
                  position: positionObject,
                  "is-touch-end-enabled": isTouchEndEnabled.value,
                  onHideShadow: hideShadow,
                  onOpenModal: openReactionsModal
                }, _createSlots({
                  default: _withCtx(() => [
                    (isMessageContentShown.value)
                      ? (_openBlock(), _createElementBlock("div", _mergeProps({
                          key: 0,
                          class: ["message-content", [_unref(getMessageClass)(_ctx.visibleMessages, _ctx.index, _unref(authId))]]
                        }, _toHandlers(_unref(handlers), true)), [
                          _renderSlot(_ctx.$slots, "default")
                        ], 16))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, [
                  (!_unref(getIsAutomaticMessageType)(_ctx.message) && !_ctx.message.isDeleted)
                    ? {
                        name: "checkboxSelectMessage",
                        fn: _withCtx(() => [
                          _createVNode(CheckboxSelectMessage, { message: _ctx.message }, null, 8, ["message"])
                        ]),
                        key: "0"
                      }
                    : undefined
                ]), 1032, ["message", "is-active", "has-shadow", "is-modal-open", "modal-id", "element-id", "position", "is-touch-end-enabled"])
              ], 2),
              _renderSlot(_ctx.$slots, "imageAttachment"),
              _renderSlot(_ctx.$slots, "videoPlayer")
            ])
          ], 2)
        ], 10, _hoisted_2))
      : _createCommentVNode("", true),
    _createVNode(BackdropModal, { enabled: isReactionsModalOpen.value }, {
      default: _withCtx(() => [
        (isReactionsModalOpen.value && !_unref(getIsAutomaticMessageType)(_ctx.message))
          ? (_openBlock(), _createBlock(ChatMessageReactionsModal, {
              key: 0,
              id: reactionsModalId.value,
              message: _ctx.message,
              onCloseReactionsModal: closeReactionsModal
            }, null, 8, ["id", "message"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["enabled"])
  ], 64))
}
}

})